import { getToolName, isToolUIPart } from "ai";
import type { UIMessage } from "ai";

export type ToolEntry =
  { name: string; output: unknown } | { failed: true; name: string };

export interface ConversationEntry {
  id: string;
  role: "user" | "assistant";
  text: string;
  tools: Array<ToolEntry>;
}

/**
 * Builds a conversation snapshot for feedback: every message from session start
 * up to and including the rated message, flattened to `{ id, role, text, tools }`.
 *
 * Text parts are joined with blank lines (a message can hold multiple text
 * parts when text is interleaved with tool calls). Successful tool calls
 * contribute their name and opaque output; failed tool calls contribute
 * only their name and a `failed` marker (no output — failure detail stays
 * in the server log). Returns an empty array if `upToMessageId` is not found.
 */
function flattenConversationSnapshot(
  messages: Array<UIMessage>,
  upToMessageId: string
): Array<ConversationEntry> {
  const idx = messages.findIndex((message) => message.id === upToMessageId);
  if (idx === -1) {
    return [];
  }

  return messages
    .slice(0, idx + 1)
    .filter(
      (message): message is UIMessage & { role: "user" | "assistant" } => {
        // System messages live server-side and never reach this client-side
        // message list; guard the invariant so one can never be silently
        // mislabeled as an assistant turn in the snapshot.
        if (message.role === "system") {
          console.error(
            `flattenConversationSnapshot: unexpected system message ${message.id} dropped from snapshot`
          );
          return false;
        }
        return true;
      }
    )
    .map((message) => {
      const text = message.parts
        .filter((part) => part.type === "text")
        .map((part) => (part as { type: "text"; text: string }).text)
        .join("\n\n");

      const tools = message.parts
        .filter(isToolUIPart)
        .filter(
          (part) =>
            part.state === "output-available" || part.state === "output-error"
        )
        .map((part): ToolEntry =>
          part.state === "output-error"
            ? { failed: true, name: getToolName(part) }
            : { name: getToolName(part), output: part.output }
        );

      return {
        id: message.id,
        role: message.role,
        text,
        tools
      };
    });
}

export { flattenConversationSnapshot };
